import { useMemo } from 'react';

import { useMerchantsListQuery } from '~entities/merchant';
import type { FindPaymentsRequestParams } from '~entities/payment';
import { useFindPaymentsQuery } from '~entities/payment';
import type { Pos } from '~entities/pos';
import { useGetPointsOfSalesQuery } from '~entities/pos';

import type { PaymentTableData, PosTableData } from './payments-table-data';

type SuccessfulReturn = {
  isSuccess: true;
  data: { payments: PaymentTableData[]; total: number };
};

type UnsuccessfulReturn = {
  isSuccess: false;
  data: undefined;
};

type DefaultReturn = {
  isLoading: boolean;
  isError: boolean;
};

type UsePaymentTableReturn = DefaultReturn &
  (SuccessfulReturn | UnsuccessfulReturn);

// query for getting enriched data to display in payments table
export function usePaymentsTableDataQuery(
  requestOptions?: FindPaymentsRequestParams
): UsePaymentTableReturn {
  const queries = [
    useFindPaymentsQuery(requestOptions!, { enabled: !!requestOptions }),
    useMerchantsListQuery(),
    useGetPointsOfSalesQuery(),
  ] as const;
  const [paymentsQuery, merchantsQuery, posQuery] = queries;

  const posMap = useMemo(
    () =>
      posQuery.data?.reduce(
        (result, pos) => {
          result[pos.id] = pos;

          return result;
        },
        {} as Record<string, Pos>
      ) ?? {},
    [posQuery.data]
  );

  const isQueryLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);

  const isLoading =
    isQueryLoading || (isFetching && paymentsQuery.isPreviousData);

  if (
    posQuery.isSuccess &&
    paymentsQuery.isSuccess &&
    merchantsQuery.isSuccess
  ) {
    const { payments, total } = paymentsQuery.data;
    const shouldAddMerchants = merchantsQuery.data.length > 1;
    const enrichedPayments: PaymentTableData[] = payments.map((payment) => {
      const pos: { pos: PosTableData } | undefined = payment.pointOfSalesId
        ? {
            pos: {
              ...posMap[payment.pointOfSalesId],
              merchantName: shouldAddMerchants
                ? posMap[payment.pointOfSalesId].merchantName
                : undefined,
            },
          }
        : undefined;

      return {
        ...payment,
        ...pos,
      };
    });

    return {
      isSuccess: true,
      data: {
        payments: enrichedPayments,
        total,
      },
      isLoading,
      isError,
    };
  }

  return {
    isSuccess: false,
    data: undefined,
    isLoading,
    isError,
  };
}
